import React, { useState } from 'react';
import {
  Alert,
  Button,
  Color,
  Divider,
  Flex,
  FontAwesome,
  Link,
  Text,
  Toggle,
} from 'component-library';
import { buildSupportMailtoHref } from 'pages/dashboard/components/Resources/ResourcesContainer';
import { observer } from 'mobx-react';
import {
  useCommonStores,
  useFeatureFlags,
  useTaxCreditsStores,
} from 'stores/useStores';
import { Page } from '../../../lib/constants';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  shareAssessmentButton: {
    backgroundColor: `${Color.blue._20}`,
    color: `${Color.blue._60}`,
    '&:hover': {
      color: `${Color.neutral.white}`,
    },
  },
  shareAssessmentButtonTxt: {
    color: `inherit`,
    '&:hover': {
      color: `inherit`,
    },
  },
  icon: {
    color: `inherit !important`,
    '&:hover': {
      color: `inherit !important`,
    },
  },
}));

interface SurveyFlowContainerProps {
  children: React.ReactElement | JSX.Element;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onContinue: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  dataTestId?: string;
  continueText?: string;
  onSkip?: () => void;
  currentPage?: Page;
  // Survey opt-out (the submittable set-aside — distinct from skip, which
  // means "come back later"). Both handlers must be passed for the toggle
  // to render; R&D sections pass neither (excluded from opt-out), and
  // DQ'd sections pass neither (DQ takes precedence, OQ-9).
  //
  // The toggle is deliberately local-only: onOptOut/onReopen flip pending
  // state in the parent (instant, no network) and the questions collapse;
  // the parent persists the choice when the customer clicks Continue.
  onOptOut?: () => void;
  onReopen?: () => void;
  isOptedOut?: boolean;
  isOptOutLocked?: boolean;
  // DQ'd sections hide the toggle (OQ-9); this renders the explanation in
  // its place instead of leaving a conspicuously missing control.
  isDisqualified?: boolean;
  // Second sentence of the DQ banner. Defaults to "re-answer the questions
  // below" — override on pages whose own questions don't decide the DQ
  // (R&D Supplies & Services / Employees point at Company Details instead).
  dqReanswerHint?: string;
}

export const SurveyFlowContainer = observer(
  ({
    children,
    title,
    subtitle,
    onBack,
    onContinue,
    isDisabled,
    isLoading,
    dataTestId,
    continueText,
    currentPage,
    onSkip,
    onOptOut,
    onReopen,
    isOptedOut,
    isOptOutLocked,
    isDisqualified,
    dqReanswerHint = 'If you believe otherwise, you can re-answer the questions below.',
  }: SurveyFlowContainerProps) => {
    const { app } = useCommonStores();
    const { surveyFlow, unifiedTaxCredits } = useTaxCreditsStores();
    const featureFlags = useFeatureFlags();
    const classes = useStyles();
    const { companyStore } = useCommonStores();

    // The locked toggle has no disabled styling (componentLibrary Toggle has
    // no disabled prop yet), so a swallowed click needs feedback: the first
    // locked click reveals the explanation below instead of doing nothing.
    const [showLockedNotice, setShowLockedNotice] = useState(false);

    const showSkipButton =
      featureFlags.showSurveySkipButton && !companyStore.accessToken;
    // Same gate as skip (OQ-4): owners/admins only, invitees see neither
    const showOptOutToggle =
      !!onOptOut && !!onReopen && !companyStore.accessToken;

    const supportMailtoHref = buildSupportMailtoHref(
      companyStore.currentCompany?.name,
      companyStore.currentCompany?.id,
    );

    const handleOptOutToggle = () => {
      if (isOptOutLocked) {
        setShowLockedNotice(true);
        return;
      }
      if (isOptedOut) {
        onReopen?.();
      } else {
        onOptOut?.();
      }
    };
    const showShareAssessmentButton =
      !!currentPage &&
      featureFlags.showShareAssessment &&
      !companyStore.accessToken;

    const shareAssessmentLabel = (
      <Flex alignItems={'center'} gap={8}>
        <FontAwesome
          name='fa-regular fa-user-plus'
          size={15}
          className={classes.icon}
        />
        <Text
          text='Share Assessment'
          className={classes.shareAssessmentButtonTxt}
        />
      </Flex>
    );

    return (
      <Flex
        direction='column'
        padding={[40, 24, 80, 24]}
        gap={24}
        dataTestId={dataTestId}
      >
        <Flex direction='column' gap={8}>
          <Flex
            justifyContent='space-between'
            alignItems='center'
            wrap='wrap'
            gap={8}
          >
            <Text text={title} variant='medium' size={app.isMobile ? 23 : 32} />
            {showOptOutToggle && (
              <Flex
                gap={8}
                alignItems='center'
                justifyContent='flex-end'
                basis={220}
              >
                <Text
                  text='Include this credit'
                  size={app.isMobile ? 13 : 15}
                />
                <Toggle
                  checked={!isOptedOut}
                  onChange={handleOptOutToggle}
                  dataTestId='survey-opt-out-toggle'
                />
              </Flex>
            )}
          </Flex>
          {subtitle && <Text text={subtitle} size={app.isMobile ? 13 : 15} />}
          {/* DQ'd sections have no toggle (OQ-9), so say why instead of
              looking like the toggle failed to load. Copy mirrors the review
              page's AssessmentStatusCard ineligible card. Informational, not
              caution: nothing is wrong and no action is required. Shown to
              invitees too. On a locked (submitted) year the re-answer
              invitation is dropped: post-submit answer edits are an
              unguarded path (hardening item 7), so don't invite them. */}
          {isDisqualified && (
            <Alert
              inCardBorder='left'
              type='info'
              variant='in_card'
              dataTestId='survey-dq-banner'
              text={
                <Text size={13}>
                  Based on the details you provided, you are ineligible for this
                  credit.
                  {!isOptOutLocked && ` ${dqReanswerHint}`}
                </Text>
              }
            />
          )}
          {/* not gated on the toggle: invitees see no toggle but still need
              the explanation for why the questions are collapsed */}
          {isOptedOut && (
            <Alert
              inCardBorder='left'
              type='caution'
              variant='in_card'
              dataTestId='survey-opt-out-warning'
              text={
                <Text size={13}>
                  This credit won&apos;t be included in the tax forms we
                  prepare.
                  {!isOptOutLocked &&
                    ' Your choice is saved when you continue, and you can switch it back on any time before you submit.'}
                  {isOptOutLocked && (
                    <>
                      {' '}
                      This year&apos;s assessment has been submitted, so this
                      choice is locked.{' '}
                      <Link
                        href={supportMailtoHref}
                        text='Contact support'
                        dataTestId='survey-opt-out-locked-support-link'
                        omitIcon
                      />{' '}
                      to request re-opening this credit.
                    </>
                  )}
                </Text>
              }
            />
          )}
          {/* Feedback for a click on the locked toggle in the ON position;
              the OFF position's alert above already carries the support
              line, so this only renders while the questions are visible. */}
          {showLockedNotice && !isOptedOut && (
            <Alert
              inCardBorder='left'
              type='caution'
              variant='in_card'
              dataTestId='survey-opt-out-locked-notice'
              text={
                <Text size={13}>
                  This year&apos;s assessment has been submitted, so credit
                  choices are locked.{' '}
                  <Link
                    href={supportMailtoHref}
                    text='Contact support'
                    dataTestId='survey-opt-out-locked-support-link'
                    omitIcon
                  />{' '}
                  to request a change.
                </Text>
              }
            />
          )}
        </Flex>

        <Divider variant='no-bottom-margin' />

        {/* an opted-out section collapses its questions; the pending choice
            is persisted by the parent's Continue handler */}
        {!isOptedOut && (
          <Flex direction='column' gap={40} padding={[0, 0, 40, 0]}>
            {children}
          </Flex>
        )}

        <Flex justifyContent='space-between' gap={40}>
          {onBack ? (
            <Button
              label='Back'
              variant='outlined'
              onClick={onBack}
              dataTestId='survey-back-btn'
            />
          ) : (
            <></>
          )}
          <Flex justifyContent='flex-end' gap={8}>
            {onSkip && showSkipButton && !isOptedOut && (
              <Button
                label={`Skip - I'll do this later`}
                onClick={onSkip}
                dataTestId='survey-skip-btn'
                variant='tertiary'
                loading={surveyFlow.surveySkipLoading}
              />
            )}
            {showShareAssessmentButton && (
              <Button
                label={shareAssessmentLabel}
                className={classes.shareAssessmentButton}
                onClick={() => {
                  unifiedTaxCredits.setShowShareAssessmentModal(
                    true,
                    currentPage,
                  );
                }}
                dataTestId={'share-assessment-btn'}
              />
            )}
            <Button
              label={continueText || 'Continue'}
              onClick={onContinue}
              // an opted-out section has nothing left to complete, so the
              // completion gating does not apply
              disabled={isOptedOut ? false : isDisabled}
              loading={isLoading || surveyFlow.surveyOptOutLoading}
              dataTestId='survey-continue-btn'
            />
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
